(() => {
  const DESKTOP_MEDIA = '(min-width: 990px)';
  const CLOSE_DELAY_MS = 180;

  function cancelClose(wrapper) {
    if (!wrapper?._sunsterHeaderCloseTimer) return;
    window.clearTimeout(wrapper._sunsterHeaderCloseTimer);
    wrapper._sunsterHeaderCloseTimer = null;
  }

  function scheduleClose(wrapper) {
    if (!wrapper) return;
    cancelClose(wrapper);
    wrapper._sunsterHeaderCloseTimer = window.setTimeout(() => {
      closeAllEntries(wrapper);
      wrapper._sunsterHeaderCloseTimer = null;
    }, CLOSE_DELAY_MS);
  }

  function escapeHtml(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  function buildProductCardMarkup(product) {
    const title = escapeHtml(product?.title || '');
    const productHandle = product?.handle ? encodeURIComponent(product.handle) : '';
    const productUrl = productHandle ? `/products/${productHandle}` : '#';
    const imageSrc = product?.images?.[0];
    const imageMarkup = imageSrc
      ? `<img class="sunster-header__product-card-image" loading="lazy" alt="${title}" src="${escapeHtml(imageSrc)}" />`
      : '<svg aria-hidden="true" class="sunster-header__product-card-placeholder placeholder-svg" viewBox="0 0 525 525"><path d="M324.5 212.7H203c-1.6 0-3.2.2-4.8.6l-58 17.8c-10.5 3.2-17.8 12.8-17.8 23.8V420c0 13.3 10.7 24 24 24h277c13.3 0 24-10.7 24-24V255.4c0-11-7.3-20.6-17.8-23.8l-58-17.8c-1.6-.4-3.2-.6-4.8-.6zm88 194.3c0 5.5-4.5 10-10 10h-277c-5.5 0-10-4.5-10-10V255.4c0-4.5 2.9-8.4 7.2-9.7l58-17.8c.6-.2 1.3-.3 2-.3h121.5c.7 0 1.4.1 2 .3l58 17.8c4.3 1.3 7.2 5.2 7.2 9.7V407zm-191.8-80.8l37.5-45.4 16.1 20 5.4 6.7 10.6-8.5-5.4-6.7-26.5-33c-1.4-1.7-3.5-2.6-5.7-2.6-2.2 0-4.3 1-5.7 2.7l-43.7 52.9-13.1-15.9c-1.4-1.7-3.5-2.6-5.7-2.6-2.2 0-4.3 1-5.7 2.7l-24.6 29.8 10.8 8.4 18.9-22.9 19.5 23.6c1.4 1.7 3.5 2.6 5.7 2.6.1 0 .1 0 .2 0 2.2 0 4.3-1 5.7-2.7z" fill="currentColor"></path></svg>';

    return `
      <a class="sunster-header__product-card" href="${productUrl}">
        <div class="sunster-header__product-card-media">
          ${imageMarkup}
        </div>
        <span class="sunster-header__product-card-title">${title}</span>
      </a>
    `;
  }

  async function ensureMerchLoaded(grid) {
    if (!grid || grid.dataset.hasMerch === 'true' || grid.dataset.loading === 'true') return;

    const collectionHandle = grid.dataset.panelCollectionHandle;
    if (!collectionHandle) return;

    grid.dataset.loading = 'true';

    try {
      const response = await fetch(`/collections/${encodeURIComponent(collectionHandle)}/products.json?limit=6`);
      if (!response.ok) return;

      const data = await response.json();
      const products = Array.isArray(data?.products) ? data.products : [];
      if (!products.length) return;

      grid.innerHTML = products.slice(0, 6).map(buildProductCardMarkup).join('');
      grid.dataset.hasMerch = 'true';
    } catch (_error) {
      return;
    } finally {
      delete grid.dataset.loading;
    }
  }

  function setActivePanel(details, childKey, merchKey) {
    if (!details || !childKey) return;

    const activeMerchKey = merchKey || childKey;

    details.dataset.activeChildKey = childKey;
    details.dataset.activeMerchKey = activeMerchKey;

    details.querySelectorAll('[data-panel-group]').forEach((group) => {
      group.classList.toggle('is-active', group.dataset.panelGroup === childKey);
    });

    details.querySelectorAll('[data-panel-merch]').forEach((merch) => {
      const isActive = merch.dataset.panelMerch === activeMerchKey;
      merch.classList.toggle('is-active', isActive);
      merch.hidden = !isActive;
    });

    const activeMerch = details.querySelector(`[data-panel-merch="${activeMerchKey}"]`);
    void ensureMerchLoaded(activeMerch);
  }

  function getDefaultPanelState(details) {
    const defaultTrigger = details.querySelector('.sunster-header__panel-group-trigger[data-panel-trigger]');
    const childKey = defaultTrigger?.dataset.panelTrigger || '';
    const merchKey = defaultTrigger?.dataset.panelMerchTarget || childKey;

    return { childKey, merchKey };
  }

  function syncOpenState(wrapper) {
    const hasOpenMenu = [...wrapper.querySelectorAll('.sunster-header__menu-details')].some((details) =>
      details.hasAttribute('open')
    );

    wrapper.classList.toggle('is-nav-open', hasOpenMenu);
  }

  function syncPanelAlignment(wrapper) {
    if (!wrapper || !window.matchMedia(DESKTOP_MEDIA).matches) return;

    const anchorEntry = wrapper.querySelector('.sunster-header__menu-entry');
    const anchorDetails = anchorEntry?.querySelector('.sunster-header__menu-details');
    const anchorSummary = anchorDetails?.querySelector('summary');
    const anchorSummaryLabel = anchorSummary?.querySelector('span');
    const panelInner = anchorDetails?.querySelector('.sunster-header__panel-inner');
    const shell = wrapper.querySelector('.sunster-header__shell');
    const anchorTarget = anchorSummaryLabel || anchorSummary;

    if (!anchorTarget || !shell || !panelInner) return;

    const anchorStart = anchorTarget.getBoundingClientRect().left;
    const panelContentStart = shell.getBoundingClientRect().left + parseFloat(window.getComputedStyle(panelInner).paddingLeft || '0');
    const anchorOffset = Math.max(0, anchorStart - panelContentStart);

    wrapper.style.setProperty('--sunster-header-panel-anchor-offset', `${anchorOffset}px`);
  }

  function closeEntry(entry) {
    const details = entry.querySelector('.sunster-header__menu-details');
    const summary = details?.querySelector('summary');
    if (!details) return;

    details.removeAttribute('open');
    summary?.setAttribute('aria-expanded', 'false');
  }

  function closeAllEntries(wrapper) {
    wrapper.querySelectorAll('.sunster-header__menu-entry').forEach(closeEntry);
    syncOpenState(wrapper);
  }

  function resetMenuFit(wrapper) {
    if (!wrapper) return;

    wrapper.style.removeProperty('--sunster-header-menu-font-size');
    wrapper.style.removeProperty('--sunster-header-menu-gap');
    wrapper.style.removeProperty('--sunster-header-menu-padding-x');
  }

  function applyMenuFit(wrapper, nav, list) {
    const menuEntry = wrapper.querySelector('.sunster-header__menu-link, .sunster-header__menu-summary');
    if (!menuEntry || !nav || !list) return;

    const currentFontSize = parseFloat(window.getComputedStyle(menuEntry).fontSize) || 18;
    const currentGap = parseFloat(window.getComputedStyle(list).gap) || 8;
    const currentPaddingX =
      parseFloat(window.getComputedStyle(menuEntry).paddingLeft) + parseFloat(window.getComputedStyle(menuEntry).paddingRight);

    if (!nav.clientWidth || !list.scrollWidth || list.scrollWidth <= nav.clientWidth) return;

    const scale = Math.max(0.74, nav.clientWidth / list.scrollWidth);
    wrapper.style.setProperty('--sunster-header-menu-font-size', `${currentFontSize * scale}px`);
    wrapper.style.setProperty('--sunster-header-menu-gap', `${currentGap * scale}px`);
    wrapper.style.setProperty('--sunster-header-menu-padding-x', `${Math.max(2, (currentPaddingX / 2) * scale)}px`);
  }

  function syncMenuFit(wrapper) {
    if (!wrapper) return;

    const nav = wrapper.querySelector('.header__inline-menu');
    const list = wrapper.querySelector('.header__inline-menu .list-menu--inline');
    if (!nav || !list) return;

    resetMenuFit(wrapper);

    if (!window.matchMedia(DESKTOP_MEDIA).matches) return;

    window.requestAnimationFrame(() => {
      applyMenuFit(wrapper, nav, list);

      window.requestAnimationFrame(() => {
        applyMenuFit(wrapper, nav, list);

        window.requestAnimationFrame(() => {
          applyMenuFit(wrapper, nav, list);
          syncPanelAlignment(wrapper);
        });
      });
    });
  }

  function openEntry(wrapper, entry) {
    if (!window.matchMedia(DESKTOP_MEDIA).matches) return;
    cancelClose(wrapper);

    wrapper.querySelectorAll('.sunster-header__menu-entry').forEach((item) => {
      if (item === entry) return;
      closeEntry(item);
    });

    const details = entry.querySelector('.sunster-header__menu-details');
    const summary = details?.querySelector('summary');
    if (!details) return;

    details.setAttribute('open', '');
    summary?.setAttribute('aria-expanded', 'true');
    const { childKey, merchKey } = getDefaultPanelState(details);
    setActivePanel(details, details.dataset.activeChildKey || childKey, details.dataset.activeMerchKey || merchKey);
    syncPanelAlignment(wrapper);
    syncOpenState(wrapper);
  }

  function bindChildTriggers(details) {
    details.querySelectorAll('[data-panel-trigger]').forEach((trigger) => {
      const childKey = trigger.dataset.panelTrigger;
      const merchKey = trigger.dataset.panelMerchTarget || childKey;
      const activate = () => setActivePanel(details, childKey, merchKey);

      trigger.addEventListener('mouseenter', activate);
      trigger.addEventListener('focus', activate);

      if (trigger.tagName === 'BUTTON') {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          activate();
        });
      }
    });
  }

  function bindEntry(wrapper, entry) {
    const details = entry.querySelector('.sunster-header__menu-details');
    const summary = details?.querySelector('summary');
    const panel = details?.querySelector('.sunster-header__panel');
    if (!details || !summary) return;

    bindChildTriggers(details);

    entry.addEventListener('mouseenter', () => cancelClose(wrapper));
    entry.addEventListener('mouseenter', () => openEntry(wrapper, entry));
    entry.addEventListener('focusin', () => openEntry(wrapper, entry));
    entry.addEventListener('mouseleave', () => scheduleClose(wrapper));

    panel?.addEventListener('mouseenter', () => cancelClose(wrapper));
    panel?.addEventListener('mouseleave', () => scheduleClose(wrapper));

    details.addEventListener('toggle', () => {
      if (details.open) {
        openEntry(wrapper, entry);
      } else {
        summary.setAttribute('aria-expanded', 'false');
        syncOpenState(wrapper);
      }
    });
  }

  function initHeader(wrapper) {
    if (!wrapper || wrapper.dataset.sunsterHeaderReady === 'true') return;

    const mobileMenuDetails = wrapper.querySelector('.sunster-mobile-menu');

    if (mobileMenuDetails) {
      mobileMenuDetails.addEventListener('toggle', () => {
        const isOpen = mobileMenuDetails.open;
        wrapper.classList.toggle('is-mobile-menu-open', isOpen);
        document.body.classList.toggle('overflow-hidden-mobile', isOpen);

        const sectionHeader = wrapper.closest('.section-header');
        if (sectionHeader) {
          sectionHeader.classList.toggle('menu-open', isOpen);
        }

        if (!isOpen) {
          mobileMenuDetails.querySelectorAll('.sunster-mobile-menu__group[open], .sunster-mobile-menu__subgroup[open]').forEach(
            (details) => details.removeAttribute('open')
          );
        }
      });
    }

    wrapper.addEventListener('mouseenter', () => cancelClose(wrapper));
    wrapper.querySelectorAll('.sunster-header__menu-entry').forEach((entry) => {
      bindEntry(wrapper, entry);
    });

    wrapper.addEventListener('mouseleave', () => {
      if (!window.matchMedia(DESKTOP_MEDIA).matches) return;
      scheduleClose(wrapper);
    });

    const mediaQuery = window.matchMedia(DESKTOP_MEDIA);
    const onMediaChange = (event) => {
      if (!event.matches) {
        closeAllEntries(wrapper);
        resetMenuFit(wrapper);
        return;
      }

      syncMenuFit(wrapper);
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onMediaChange);
    }

    const resizeHandler = () => syncMenuFit(wrapper);
    window.addEventListener('resize', resizeHandler);
    wrapper._sunsterHeaderResizeHandler = resizeHandler;

    wrapper.dataset.sunsterHeaderReady = 'true';
    syncMenuFit(wrapper);
    syncPanelAlignment(wrapper);
  }

  function init(root = document) {
    root.querySelectorAll('.header-wrapper--sunster').forEach(initHeader);
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.header-wrapper--sunster').forEach((wrapper) => {
      cancelClose(wrapper);
      document.body.classList.remove('overflow-hidden-mobile');
      wrapper.classList.remove('is-mobile-menu-open');

      if (wrapper._sunsterHeaderResizeHandler) {
        window.removeEventListener('resize', wrapper._sunsterHeaderResizeHandler);
      }

      delete wrapper._sunsterHeaderResizeHandler;
      delete wrapper.dataset.sunsterHeaderReady;
    });
  });
})();
